import { useState } from 'react'
import { StyleProp, ViewStyle } from 'react-native'
import { FAB } from 'react-native-paper'
import { StyleSheet } from 'react-native-unistyles'

import NewAdoptionAction from '@/components/fosters/actions/NewAdoptionAction'
import ReturnAdoptionAction from '@/components/fosters/actions/ReturnAdoptionAction'
import TourSpot from '@/components/tour/TourSpot'
import { useFosterActions } from '@/hooks/useFosterActions'

type FosterActionButtonProps = {
  fosterId: string
  style?: StyleProp<ViewStyle>
}

const LABEL_MAX_FONT_SIZE_MULTIPLIER = 1.5
/** Diameter of Paper's default FAB button */
const FAB_SIZE = 56
/** Paper's FAB.Group adds this margin around the main FAB button */
const FAB_MARGIN = 16

const FosterActionButton = ({ fosterId, style }: FosterActionButtonProps) => {
  const {
    actions,
    isSubscriptionActive,
    newAdoptionRecordOpen,
    setNewAdoptionRecordOpen,
    returnAdoptionOpen,
    setReturnAdoptionOpen,
  } = useFosterActions(fosterId)

  const [state, setState] = useState({ open: false })
  const onStateChange = ({ open }: { open: boolean }) => setState({ open })

  const { open } = state

  const fabActions = actions.map((action) => ({
    color: action.color,
    icon: action.icon,
    label: action.label,
    labelMaxFontSizeMultiplier: LABEL_MAX_FONT_SIZE_MULTIPLIER,
    onPress: action.onPress,
    style: styles.iconButton,
    testID: action.testID.replace('-button', '-action'),
  }))

  // Extract padding from style so the TourSpot marker aligns with the FAB.
  // FAB.Group uses these as internal padding, placing the 56×56 button at
  // (bottom: paddingBottom, right: paddingRight) within its container.
  const flat = StyleSheet.flatten(style) || {}
  const pr = typeof flat.paddingRight === 'number' ? flat.paddingRight : 0
  const pb = typeof flat.paddingBottom === 'number' ? flat.paddingBottom : 0

  return (
    <>
      <FAB.Group
        actions={fabActions}
        icon={open ? 'close' : 'menu'}
        onStateChange={onStateChange}
        open={open}
        style={style}
        testID="foster-action-button"
        visible={isSubscriptionActive}
      />

      {/* Tour marker — same positioning context as FAB.Group so bottom/right align */}
      <TourSpot
        id="foster-fab-button"
        pointerEvents="none"
        style={{
          bottom: pb + FAB_MARGIN,
          height: FAB_SIZE,
          position: 'absolute',
          right: pr + FAB_MARGIN,
          width: FAB_SIZE,
        }}
      />

      <NewAdoptionAction
        fosterId={fosterId}
        onClose={() => setNewAdoptionRecordOpen(false)}
        open={newAdoptionRecordOpen}
      />
      <ReturnAdoptionAction
        fosterId={fosterId}
        onClose={() => setReturnAdoptionOpen(false)}
        open={returnAdoptionOpen}
      />
    </>
  )
}

const styles = StyleSheet.create((theme) => ({
  iconButton: {
    backgroundColor: theme.colors.surface,
  },
}))

export default FosterActionButton
